import React, { useEffect, useState } from 'react';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import dayjs from 'dayjs';
import uuid from '@afosto/utils/uuid';
import { Link, useParams } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import Page from '@afosto/app-components-react/Page';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import CouponCodeForm from '../../components/CouponCodeForm';
import DataGrid from '@afosto/app-components-react/DataGrid';
import { coupons as couponsApi } from '@afosto/order/api/Coupons';
import translations from './translations';
import { useSnackbar } from 'notistack';
import CouponCodePreview from '../../components/CouponCodePreview';

const fetchCoupons = id => async dataSource =>
  dataSourceRequest(
    dataSource,
    async (data, requestOptions) => couponsApi.getCoupons({ categoryId: id }, requestOptions),
    {
      transformData: data =>
        data.reduce(
          (acc, row) => [
            ...acc,
            {
              id: row.code,
              code: row.code,
              categoryId: row.category_id,
              createdAt: row.created_at,
              expiresAt: row.expires_at,
              isUnlimited: row?.usage?.is_unlimited,
              remaining: row?.usage?.remaining,
              claims: row?.claims,
            },
          ],
          [],
        ),
    },
  );

const CouponCategoryView = () => {
  const intl = useIntl();
  const { couponCategoryID } = useParams();
  const [isLoadingCategory, setIsLoadingCategory] = useState(true);
  const [category, setCategory] = useState(null);
  const [couponCode, setCouponCode] = useState(null);
  const [showCreateCoupon, setShowCreateCoupon] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const dataSource = useDataGrid(fetchCoupons(couponCategoryID), {
    modules: ['dataProperties', 'pagination', 'activeItem'],
    pagination: {
      itemsPerPage: 100,
    },
    dataProperties: {
      fixedProperties: [
        {
          key: 'code',
          name: intl.formatMessage({
            id: 'dataGrid.columns.coupons.code',
            defaultMessage: 'Code',
            description: 'The column name for column name',
          }),
        },
        {
          key: 'isUnlimited',
          name: intl.formatMessage({
            id: 'dataGrid.columns.couponCodes.isUnlimited',
            defaultMessage: 'Unlimited',
            description: 'The column name for column unlimited',
          }),
        },
        {
          key: 'remaining',
          name: intl.formatMessage({
            id: 'dataGrid.columns.couponCodes.remaining',
            defaultMessage: 'Remaining',
            description: 'The column name for column remaining',
          }),
        },
        {
          key: 'expiresAt',
          name: intl.formatMessage({
            id: 'dataGrid.columns.couponCodes.expiresAt',
            defaultMessage: 'Expires at',
            description: 'The column name for column expires at',
          }),
          type: 'datetime',
        },
        {
          key: 'createdAt',
          name: intl.formatMessage({
            id: 'dataGrid.columns.couponCodes.updatedAt',
            defaultMessage: 'Updated at',
            description: 'The column name for column name',
          }),
          type: 'datetime',
        },
      ],
    },
  });

  const { activeItem: activeItemModule } = dataSource;
  const { activeItem, setActiveItem } = activeItemModule;

  const createCouponCode = async formData => {
    try {
      const formattedData = {
        code: formData?.code,
        expires_at: formData?.expiresAt,
        usage: {
          is_unlimited: formData?.isUnlimited,
          remaining: Number(formData?.remaining),
        },
        category_id: category?.id,
      };
      const response = await couponsApi.createCoupons(formattedData);
      setShowCreateCoupon(false);
      dataSource.reloadDataSource();
      enqueueSnackbar(intl.formatMessage(translations.feedback.couponCreated), {
        variant: 'success',
      });

      return response;
    } catch (e) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.couponNotCreated), {
        variant: 'error',
      });

      return e;
    }
  };

  const handleQuickAddCouponCode = () =>
    createCouponCode({
      code: uuid(),
      expiresAt: dayjs().add(category?.defaults?.expires_after, 'seconds'),
      isUnlimited: category?.defaults?.usage?.is_unlimited,
      remaining: category?.defaults?.usage?.remaining,
    });

  const handleDisableCouponCode = async id => {
    try {
      await couponsApi.disableCoupon(id);
      dataSource.reloadDataSource();
      setActiveItem(null);
      enqueueSnackbar(intl.formatMessage(translations.feedback.couponDisabled), {
        variant: 'success',
      });
    } catch (e) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.couponNotDisabled), {
        variant: 'error',
      });
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchCategory = async id => {
      try {
        setIsLoadingCategory(true);
        const categoryResponse = await couponsApi.getCouponCategory(id);
        const { data } = categoryResponse;
        if (isMounted) {
          setCategory(data);
        }
      } catch (e) {
        setCategory(null);
      } finally {
        setIsLoadingCategory(false);
      }
    };

    if (couponCategoryID) {
      fetchCategory(couponCategoryID);
    }

    return () => {
      isMounted = false;
    };
  }, [couponCategoryID, enqueueSnackbar, intl]);

  useEffect(() => {
    let isMounted = true;

    const fetchCouponCode = async id => {
      try {
        const couponCodeResponse = await couponsApi.viewCoupon(id);
        const { data } = couponCodeResponse;
        if (isMounted) {
          setCouponCode(data);
        }
      } catch (e) {
        setCouponCode(null);
      }
    };

    if (activeItem) {
      fetchCouponCode(activeItem);
    }

    return () => {
      isMounted = false;
    };
  }, [activeItem, enqueueSnackbar, intl]);

  const previewMenu = [
    {
      key: 'disable_coupon',
      label: intl.formatMessage(translations.previewActions.disableCouponCode),
      icon: 'trash-alt',
      color: 'error',
      onClick: () => handleDisableCouponCode(activeItem),
    },
  ];

  return (
    <Grid container wrap="nowrap">
      <Page
        title={category?.name}
        fullWidth
        breadcrumbs={[
          {
            label: intl.formatMessage(translations.breadcrumbs.coupons),
            path: '/coupons',
          },
          {
            label: category?.name ?? couponCategoryID,
            path: `/coupons/${couponCategoryID}`,
            active: true,
          },
        ]}
        linkComponent={Link}
        primaryAction={{
          text: intl.formatMessage(translations.create.button),
          FABVariant: 'extended',
          actions: [
            {
              text: intl.formatMessage(translations.actions.quickAdd),
              action: handleQuickAddCouponCode,
            },
            {
              text: intl.formatMessage(translations.actions.customAdd),
              action: () => setShowCreateCoupon(true),
            },
          ],
        }}
        isLoading={isLoadingCategory}
        style={{ flex: 1 }}
      >
        <DataGrid dataSource={dataSource} />
      </Page>
      <FormDrawer
        title={intl.formatMessage(translations.createCoupon.title)}
        drawerWidth="420px"
        isOpen={showCreateCoupon}
        onClose={() => setShowCreateCoupon(false)}
      >
        <CouponCodeForm
          category={category}
          onSubmit={createCouponCode}
          onCancel={() => setShowCreateCoupon(false)}
          isEditing
          inline={false}
        />
      </FormDrawer>
      <CouponCodePreview
        title={intl.formatMessage(translations.viewCoupon.title)}
        isOpen={activeItem}
        couponCode={couponCode}
        category={category}
        onClose={() => setActiveItem(null)}
        previewMenu={previewMenu}
      />
    </Grid>
  );
};

export default CouponCategoryView;
